import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input, Skeleton, Tag } from "antd";

import { apiFetchJson } from "@/utils/apiFetch";

import { Chip, fmtNum, MetricCard, SectionCard, useMasteryApi } from "./shared";

// Ported from the standalone Golden Explorer's Identifiers view
// (/api/identifiers → mastery-identifiers/). No cross-links to explorer
// routes — golden entities render as name + id text.

interface IdentifierStats {
  total_identifiers: number;
  n_id_types: number;
  n_golden_entities: number;
  n_conflicts: number;
  raw_observations: number;
}

interface CollisionEntity {
  golden_ext: string;
  name: string | null;
  golden_id: string | null;
}

interface SharedCollision {
  id_type: string;
  id_value: string;
  n_observations: number;
  n_raw_records: number;
  source_systems: string | null;
  entities: CollisionEntity[] | null;
}

interface CrossFgidConflict {
  conflict_id: string;
  golden_id: string | null;
  canonical_name: string | null;
  severity: string | null;
  status: string | null;
  details: Record<string, unknown> | null;
}

export interface MasteryIdentifiersPayload {
  stats: IdentifierStats;
  kinds: Array<{ entity_kind: string; n: number }>;
  dist: Array<{
    id_type: string;
    n: number;
    mean_sources: number | null;
    kind: string | null;
  }>;
  collisions: {
    shared: SharedCollision[];
    cross_fgid: CrossFgidConflict[];
  };
}

interface ResolveResult {
  id_type: string;
  id_value: string;
  kind: string;
  golden_ext: string | null;
  n_sources: number | null;
  sources: string | null;
  golden_conflict: boolean;
  entity_name: string | null;
  golden_id: string | null;
  country: string | null;
}

const KIND_COLOR: Record<string, string> = {
  site: "#0E7490",
  person: "#7C3AED",
  trial: "#0891B2",
  organization: "#D97706",
};

function useResolve(idValue: string) {
  return useQuery({
    queryKey: ["mastery", "identifier-resolve", idValue],
    queryFn: () =>
      apiFetchJson<{ results: ResolveResult[] }>(
        `${import.meta.env.VITE_API_URL}/data/portfolio/mastery-identifiers/resolve/?id_value=${encodeURIComponent(idValue)}`,
      ),
    enabled: idValue !== "",
    staleTime: 60_000,
  });
}

function ResolveRow({ r }: { r: ResolveResult }) {
  return (
    <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded border border-[#E6EEF2] bg-white p-3">
      <div>
        <Tag color={KIND_COLOR[r.kind] ?? "#64748B"}>{r.kind}</Tag>
        <span className="text-sm font-semibold text-[#2B4752]">
          {r.entity_name ?? r.golden_ext ?? r.id_value}
        </span>
        {r.country && (
          <span className="ml-2 text-sm text-[#2B4752]">{r.country}</span>
        )}
        {r.golden_conflict && (
          <Tag color="#DC2626" className="ml-2">
            collision
          </Tag>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="text-xs text-[#2B4752]">
            {r.id_type} = {r.id_value}
          </span>
          {r.sources && <Chip>{r.sources}</Chip>}
          <Chip>
            {r.n_sources ?? 0} source{r.n_sources === 1 ? "" : "s"}
          </Chip>
        </div>
      </div>
      {r.golden_id && (
        <span className="text-xs text-[#2B4752]">{r.golden_id}</span>
      )}
    </div>
  );
}

export default function MasteryIdentifiers() {
  const { data, isLoading } = useMasteryApi<MasteryIdentifiersPayload>(
    "mastery-identifiers/",
  );
  const [lookup, setLookup] = useState("");
  const { data: resolved, isFetching: resolving } = useResolve(lookup);

  if (isLoading || !data) {
    return <Skeleton active paragraph={{ rows: 12 }} />;
  }

  const st = data.stats;
  const maxType = Math.max(...data.dist.map((d) => d.n), 1);
  const kindRows = data.kinds.map((k) => ({
    label: k.entity_kind,
    value: fmtNum(k.n),
  }));

  return (
    <div>
      {/* Keyspace stats — breakdowns as first-class rows */}
      <div className="mb-4 grid grid-cols-3 gap-4">
        <MetricCard
          label="Distinct identifiers"
          value={fmtNum(st.total_identifiers)}
          rows={[
            { label: "ID types", value: fmtNum(st.n_id_types) },
            { label: "Raw observations", value: fmtNum(st.raw_observations) },
          ]}
        />
        <MetricCard
          label="Golden entities with ids"
          value={fmtNum(st.n_golden_entities)}
          rows={kindRows}
        />
        <MetricCard
          label="Collisions"
          value={fmtNum(st.n_conflicts)}
          rows={[
            {
              label: "One id, multiple entities",
              value: fmtNum(st.n_conflicts),
            },
            {
              label: "Cross-fingerprint duplicates",
              value: fmtNum(data.collisions.cross_fgid.length),
            },
          ]}
        />
      </div>

      {/* Resolver */}
      <SectionCard
        title="Resolve an external id"
        meta="Citeline / EU OMS / NPI / CTMS…"
      >
        <Input.Search
          size="large"
          placeholder="e.g. 141330 (Citeline facility id) or an NPI"
          onSearch={(v) => setLookup(v.trim())}
          allowClear
          enterButton="Resolve"
          loading={resolving}
          style={{ maxWidth: 520 }}
        />
        {lookup && resolved && (
          <div className="mt-3">
            {resolved.results.length ? (
              resolved.results.map((r) => (
                <ResolveRow
                  key={`${r.id_type}:${r.id_value}:${r.kind}:${r.golden_ext ?? ""}`}
                  r={r}
                />
              ))
            ) : (
              <p className="mt-2 text-sm text-[#2B4752]">
                No identifier matches “{lookup}”.
              </p>
            )}
          </div>
        )}
      </SectionCard>

      {/* Keyspace shape */}
      <SectionCard
        title="Identifiers by type"
        meta="mean sources > 1 = cross-source anchor"
      >
        {/* 37 id types — cap the card height and scroll inside */}
        <div className="max-h-[420px] overflow-y-auto">
          <table className="w-full border-collapse text-xs">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="text-[#556C75]">
                <th className="p-2 text-left">ID type</th>
                <th className="p-2 text-left">Kind</th>
                <th className="w-1/3 p-2 text-left"> </th>
                <th className="p-2 text-right">Identifiers</th>
                <th className="p-2 text-right">Mean sources</th>
              </tr>
            </thead>
            <tbody>
              {data.dist.map((d) => (
                <tr key={d.id_type} className="border-t border-[#f0f0f0]">
                  <td className="p-2 text-[#2B4752]">{d.id_type}</td>
                  <td className="p-2">
                    <span
                      className="font-medium"
                      style={{ color: KIND_COLOR[d.kind ?? ""] ?? "#64748B" }}
                    >
                      {d.kind ?? "—"}
                    </span>
                  </td>
                  <td className="p-2">
                    <div className="h-2.5 rounded bg-[#f1f5f9]">
                      <div
                        className="h-full rounded"
                        style={{
                          width: `${Math.max((100 * d.n) / maxType, 0.5)}%`,
                          background: KIND_COLOR[d.kind ?? ""] ?? "#64748B",
                        }}
                      />
                    </div>
                  </td>
                  <td className="p-2 text-right tabular-nums">{fmtNum(d.n)}</td>
                  <td className="p-2 text-right tabular-nums">
                    {d.mean_sources ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Collisions */}
      <div className="grid grid-cols-2 gap-4">
        <SectionCard
          title={`Collisions — one id, multiple entities · ${fmtNum(st.n_conflicts)}`}
        >
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full border-collapse text-xs">
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="text-[#556C75]">
                  <th className="p-2 text-left">Type</th>
                  <th className="p-2 text-left">Value</th>
                  <th className="p-2 text-left">Sources</th>
                  <th className="p-2 text-left">Resolves to</th>
                </tr>
              </thead>
              <tbody>
                {data.collisions.shared.map((s) => (
                  <tr
                    key={`${s.id_type}:${s.id_value}`}
                    className="border-t border-[#f0f0f0] align-top"
                  >
                    <td className="p-2 text-[#2B4752]">{s.id_type}</td>
                    <td className="p-2 text-[#2B4752]">{s.id_value}</td>
                    <td className="p-2">
                      {s.source_systems ? <Chip>{s.source_systems}</Chip> : "—"}
                    </td>
                    <td className="p-2">
                      {s.entities?.length
                        ? s.entities.map((e) => (
                            <div key={e.golden_ext} className="py-0.5">
                              <span className="text-[#2B4752]">
                                {e.name ?? e.golden_ext}
                              </span>{" "}
                              <Chip>{e.golden_id ?? e.golden_ext}</Chip>
                            </div>
                          ))
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
        <SectionCard
          title={`Cross-fingerprint duplicates · ${data.collisions.cross_fgid.length}`}
        >
          {data.collisions.cross_fgid.length ? (
            <div className="max-h-[420px] overflow-y-auto">
              <table className="w-full border-collapse text-xs">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr className="text-[#556C75]">
                    <th className="p-2 text-left">Golden record</th>
                    <th className="p-2 text-left">Severity</th>
                    <th className="p-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.collisions.cross_fgid.map((c) => (
                    <tr
                      key={c.conflict_id}
                      className="border-t border-[#f0f0f0]"
                    >
                      <td className="p-2">
                        <span className="font-semibold text-[#2B4752]">
                          {c.canonical_name ?? "—"}
                        </span>{" "}
                        {c.golden_id && <Chip>{c.golden_id}</Chip>}
                      </td>
                      <td className="p-2">
                        <Tag color="#DC2626">{c.severity ?? "dup"}</Tag>
                      </td>
                      <td className="p-2">
                        <Chip>{c.status ?? "open"}</Chip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-[#2B4752]">
              No cross-fingerprint duplicates.
            </p>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
